/**
 * Account menu.
 *
 * It answers the three questions a user has about their session: *who am I*, *where are my settings*, and *how do I
 * leave*. Signing out of every device is offered next to a normal sign-out because the two are easily confused and
 * the destructive one is the harder to find — here the difference is a single labelled entry.
 */

import { LogOut, Settings, ShieldOff, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useCurrentUser, useLogout } from '@/features/auth/hooks/useAuth';
import { initialsOf } from '@/lib/utils';

/** Renders the account entry with its menu. */
export function UserMenu() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const logout = useLogout();

  if (!user) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="rounded-full"
          aria-label={`${t('common.account')} — ${user.displayName}`}
        >
          {user.avatarUrl ? (
            // A plain `<img>` is deliberate: avatars are remote and decorative, so the optimiser buys nothing and the
            // empty `alt` keeps the accessible name on the button.
            <img src={user.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
          ) : (
            <span
              aria-hidden
              className="grid h-7 w-7 place-items-center rounded-full bg-primary-muted text-xs font-semibold text-accent-foreground"
            >
              {initialsOf(user.displayName)}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="flex flex-col gap-0.5 py-2">
          <span className="text-sm font-semibold text-foreground">{user.displayName}</span>
          <span className="text-xs font-normal text-muted-foreground">
            {t('nav.signedInAs')} {user.email ?? user.publicIdentifier ?? user.displayName}
          </span>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild>
          <Link to="/settings">
            <User aria-hidden className="h-4 w-4" />
            {t('common.profile')}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuItem asChild>
          <Link to="/settings?tab=ai">
            <Settings aria-hidden className="h-4 w-4" />
            {t('nav.settings')}
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => logout.mutate(false)}>
          <LogOut aria-hidden className="h-4 w-4" />
          {t('common.signOut')}
        </DropdownMenuItem>

        <DropdownMenuItem destructive onSelect={() => logout.mutate(true)}>
          <ShieldOff aria-hidden className="h-4 w-4" />
          {t('common.signOutAll')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
